import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { sendOTP as sendOTPApi, normalizePhone } from '../../sendOtp';
import { verifyOtpAndSignIn } from '../../services/verifyOtpAndSignIn';
import AppIcon from '../../components/AppIcon';
import BackButton from '../../components/BackButton';
import Toast from '../../components/ui/Toast';
import { Checkbox } from '../../components/ui/Checkbox';
import OtpInput from '../../components/OtpInput';

const PhotographerRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    entityType: '', // 'individual' = فرد، 'entity' = استوديو
    businessName: '',
    bio: '',
    experienceYears: '',
    specializations: [],
    city: '',
    equipmentAvailable: [],
    shootingMethods: [],      // وسائل التصوير
    shootingMethodModels: {}, // نوع الجهاز لكل وسيلة {"professional_camera":"Canon EOS R5",...}
    languagesSpoken: [],
    agreeToTerms: false
  });
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [msegatId, setMsegatId] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [toastNotification, setToastNotification] = useState(null);
  const [processingMessage, setProcessingMessage] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [termsLoading, setTermsLoading] = useState(false);

  // Document states
  const [identityDocument, setIdentityDocument] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const documentInputRef = useRef(null);
  const verifyInFlightRef = useRef(false);

  // SAFE MODE: Certificates feature - optional photography certificates
  const [certificateFiles, setCertificateFiles] = useState([]);
  const [uploadingCertificates, setUploadingCertificates] = useState(false);
  const certificateInputRef = useRef(null);
  const CERTIFICATE_ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
  const CERTIFICATE_MAX_SIZE = 5 * 1024 * 1024; // 5MB

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 6 types matching the booking flow only
  const specializations = [
    { value: 'event', label: 'تصوير المناسبات' },
    { value: 'product', label: 'تصوير المنتجات' },
    { value: 'portrait', label: 'التصوير الشخصي' },
    { value: 'food', label: 'تصوير الطعام' },
    { value: 'real-estate', label: 'تصوير العقارات' },
    { value: 'wedding', label: 'حفلات الزفاف' },
  ];

  const equipmentOptions = [
    { value: 'basic-editing',        label: 'تعديل أساسي (تصحيح إضاءة وألوان)' },
    { value: 'professional-editing', label: 'تعديل احترافي متقدم' },
  ];

  // Shooting Methods Options (same as profile page)
  const shootingMethodOptions = [
    { value: 'professional_camera', label: 'كاميرا احترافية', icon: 'Camera',  placeholder: 'مثال: Canon EOS R5, Sony A7 IV' },
    { value: 'mobile_camera',       label: 'كاميرا جوال',     icon: 'Smartphone', placeholder: 'مثال: iPhone 15 Pro, Samsung S24' },
    { value: 'drone',               label: 'تصوير جوي (درون)', icon: 'Radio',  placeholder: 'مثال: DJI Mini 4 Pro, Autel Evo' },
  ];

  const cities = [
    'الرياض',
    'جدة',
    'الدمام',
    'الخبر',
    'المدينة المنورة',
    'مكة المكرمة',
    'الطائف',
    'أبها',
    'خميس مشيط',
    'جازان',
    'نجران',
    'الباحة',
    'تبوك',
    'حائل',
    'بريدة',
    'سكاكا',
  ];

  const steps = [
    { id: 1, title: 'البيانات الأساسية', icon: 'User' },
    { id: 2, title: 'معلومات العمل', icon: 'Briefcase' },
    { id: 3, title: 'التخصصات', icon: 'Camera' },
    { id: 4, title: 'التحقق', icon: 'CheckCircle' },
  ];

  // ── تحميل محتوى الشروط من Supabase ────────────────────────────────────────
  const openTermsModal = async () => {
    setShowTermsModal(true);
    if (termsContent) return;
    setTermsLoading(true);
    try {
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'page_terms_content')
        .single();
      setTermsContent(data?.value || '');
    } catch {
      setTermsContent('');
    } finally {
      setTermsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Ensure phone number is digits-only (allowing user to type more or less than 10 for validation)
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 15);
      setFormData(prev => ({
        ...prev,
        phone: digitsOnly
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMultiSelect = (value, field = 'specializations') => {
    setFormData(prev => {
      const currentValues = prev[field] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [field]: newValues };
    });
  };

  // Document handling
  const handleDocumentSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('نوع الملف غير مدعوم');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('حجم الملف كبير جداً');
      return;
    }

    setIdentityDocument(file);
    setError('');

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setDocumentPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setDocumentPreview(null);
    }
  };

  const handleRemoveDocument = () => {
    setIdentityDocument(null);
    setDocumentPreview(null);
    if (documentInputRef.current) documentInputRef.current.value = '';
  };

  // SAFE MODE: Certificates feature - add/remove certificate files
  const handleCertificateSelect = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (!files.length) return;
    const valid = [];
    for (const file of files) {
      if (!file || !file.name) continue;
      if (!CERTIFICATE_ALLOWED_TYPES.includes(file.type)) {
        setToastNotification({ title: 'نوع غير مدعوم', message: `${file.name}: يُقبل PDF، JPG، PNG فقط`, icon: 'AlertCircle', iconColor: 'var(--color-destructive)' });
        continue;
      }
      if (file.size > CERTIFICATE_MAX_SIZE) {
        setToastNotification({ title: 'حجم كبير', message: `${file.name}: الحد الأقصى 5MB`, icon: 'AlertCircle', iconColor: 'var(--color-destructive)' });
        continue;
      }
      valid.push(file);
    }
    if (valid.length) setCertificateFiles(prev => [...prev, ...valid]);
    if (certificateInputRef.current) certificateInputRef.current.value = '';
  };
  const removeCertificateFile = (index) => {
    setCertificateFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadIdentityDocument = async (userId) => {
    if (!identityDocument || !userId) return null;

    try {
      const timestamp = Date.now();
      const ext = identityDocument.name.split('.').pop();
      const fileName = `id_document_${timestamp}.${ext}`;
      const filePath = `${userId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('verification-docs')
        .upload(filePath, identityDocument, { cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('verification-docs')
        .getPublicUrl(filePath);

      return {
        url: urlData?.publicUrl,
        type: identityDocument.type.includes('pdf') ? 'pdf' : 'image'
      };
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1: {
        const phoneDigits = (formData.phone || '').replace(/\D/g, '');
        const base = formData.fullName && formData.email && phoneDigits.length === 10;
        if (!base) return false;
        // إذا اختار "استوديو" فإن اسم الاستوديو إلزامي
        if (formData.entityType === 'entity') {
          return !!(formData.businessName && formData.businessName.trim());
        }
        return true;
      }
      case 2:
        return !!formData.city;
      case 3: {
        // Require at least one specialization, at least one shooting method with its model, agreement to terms, and identity document
        const allModelsFilledReg = formData.shootingMethods.length > 0 &&
          formData.shootingMethods.every(
            (m) => (formData.shootingMethodModels?.[m] || '').trim().length > 0
          );
        return (
          formData.specializations.length > 0 &&
          allModelsFilledReg &&
          formData.equipmentAvailable.length > 0 &&
          formData.agreeToTerms &&
          !!identityDocument
        );
      }
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
        setError('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      setError('يرجى إكمال جميع الحقول المطلوبة');
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const normalizedPhone = normalizePhone(formData.phone);

  // Send OTP via SMS (Authentica)
  const sendOTP = async () => {
    if (!normalizedPhone) {
      setError('يرجى إدخال رقم الجوال صحيح');
      return;
    }

    setLoading(true);
    setError('');
    setProcessingMessage('جاري إرسال الرمز إلى جوالك...');

    try {
      const { data: existingByPhone } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('phone', normalizedPhone)
        .maybeSingle();

      if (existingByPhone?.role === 'customer') {
        setError('هذا الرقم مسجل كحساب عميل. لا يمكن استخدامه كمصور.');
        setLoading(false);
        return;
      }
      if (existingByPhone?.role === 'admin') {
        setError('هذا الرقم مسجل كحساب مشرف.');
        setLoading(false);
        return;
      }
      if (existingByPhone?.role === 'photographer') {
        setError('هذا الرقم مسجل بالفعل كمصور. يرجى تسجيل الدخول.');
        setLoading(false);
        return;
      }

      if (formData.email?.trim()) {
        const { data: existingByEmail } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('email', formData.email.toLowerCase().trim())
          .maybeSingle();
        if (existingByEmail) {
          setError('هذا البريد مسجل بالفعل. استخدم رقم جوال مختلف أو سجّل الدخول.');
          setLoading(false);
          return;
        }
      }

      const data = await sendOTPApi(normalizedPhone);
      if (!data?.success) {
        throw new Error(data?.error || 'فشل إرسال الرمز');
      }
      setMsegatId(data.id);

      setToastNotification({
        title: 'تم إرسال كود التحقق',
        message: 'تحقق من رسالة الجوال 📱',
        icon: 'CheckCircle',
        iconColor: 'var(--color-success)'
      });

      setOtpCode('');
      setOtpSent(true);
      setCountdown(60);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'فشل إرسال كود التحقق');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndRegister = useCallback(async (codeOverride) => {
    const code = codeOverride ?? otpCode;
    if (code.length !== 4) {
      setError('يرجى إدخال الكود كاملاً');
      return;
    }

    setLoading(true);
    setError('');
    setProcessingMessage('جاري التحقق وإنشاء الحساب...');

    try {
      const data = await verifyOtpAndSignIn({
        phone: normalizedPhone,
        code,
        role: 'photographer',
        msegatId,
        userData: {
          full_name: formData.fullName?.trim() || null,
          email: formData.email?.trim() || null,
          phone: normalizedPhone,
          entity_type: formData.entityType || 'individual',
          business_name: formData.businessName || null,
          bio: formData.bio || null,
          experience_years: parseInt(formData.experienceYears, 10) || 0,
          specializations: formData.specializations.map(val => {
            const spec = specializations.find(s => s.value === val);
            return spec ? spec.label : val;
          }),
          city: formData.city || null,
          equipment_available: (formData.equipmentAvailable || []).map(val => {
            const equip = equipmentOptions.find(e => e.value === val);
            return equip ? equip.label : val;
          }),
          shooting_methods: Array.isArray(formData.shootingMethods) ? formData.shootingMethods : [],
          shooting_method_models: formData.shootingMethodModels || {},
          languages: formData.languagesSpoken || [],
        },
      });

      if (data?.error) throw new Error(data.error);

      // دالة مشتركة: ترفع الوثائق بعد المصادقة ثم تنتقل للوحة التحكم
      const finalizeRegistration = async (userId) => {
        // 1. رفع مستند الهوية إلى verification-docs وتسجيله في photographer_profiles
        if (identityDocument && userId) {
          setProcessingMessage('جاري رفع مستند الهوية...');
          try {
            const ext = identityDocument.name.split('.').pop();
            const filePath = `${userId}/id_document_${Date.now()}.${ext}`;
            const { error: uploadErr } = await supabase.storage
              .from('verification-docs')
              .upload(filePath, identityDocument, { cacheControl: '3600', upsert: false });
            if (!uploadErr) {
              const { data: urlData } = supabase.storage.from('verification-docs').getPublicUrl(filePath);
              await supabase.from('photographer_profiles').update({
                id_document_url: urlData?.publicUrl,
                id_document_type: identityDocument.type.includes('pdf') ? 'pdf' : 'image',
                verification_status: 'submitted',
                verification_submitted_at: new Date().toISOString(),
                identity_verified: false,
                identity_document_expires_at: null,
                updated_at: new Date().toISOString(),
              }).eq('user_id', userId);
              // إشعار الإدارة بطلب التوثيق
              try {
                await supabase.from('admin_notifications').insert({
                  type: 'verification_request',
                  title: '📄 طلب توثيق هوية جديد',
                  message: `المصور "${formData.fullName || ''}" رفع مستند تحقق من الهوية عند التسجيل`,
                  related_user_id: userId,
                });
              } catch (_) {}
            }
          } catch (err) {
            console.warn('Identity document upload failed (non-blocking):', err);
          }
        }

        // 2. رفع الشهادات إلى photographer-certificates وحفظها في photographer_profiles
        if (certificateFiles.length > 0 && userId) {
          setProcessingMessage('جاري رفع الشهادات...');
          try {
            const certEntries = [];
            for (const file of certificateFiles) {
              const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 100);
              const filePath = `${userId}/${Date.now()}-${safeName}`;
              const { error: certErr } = await supabase.storage
                .from('photographer-certificates')
                .upload(filePath, file, { cacheControl: '3600', upsert: false });
              if (!certErr) {
                const { data: urlData } = supabase.storage.from('photographer-certificates').getPublicUrl(filePath);
                certEntries.push({ url: urlData?.publicUrl || '', name: file.name });
              }
            }
            if (certEntries.length > 0) {
              await supabase.from('photographer_profiles').update({
                certificates: certEntries,
                updated_at: new Date().toISOString(),
              }).eq('user_id', userId);
            }
          } catch (err) {
            console.warn('Certificate upload failed (non-blocking):', err);
          }
        }

        // GA4: حدث تسجيل مصور جديد — يُستخدم كإحالة ناجحة في Google Ads
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
          window.gtag('event', 'photographer_sign_up', {
            event_category: 'registration',
            event_label: 'photographer',
            value: 1,
          });
        }

        // Snapchat Pixel: حدث إتمام تسجيل مصور
        if (typeof window !== 'undefined' && typeof window.snaptr === 'function') {
          window.snaptr('track', 'SIGN_UP');
        }

        setToastNotification({
          title: 'تم التسجيل بنجاح!',
          message: 'جاري تحويلك للوحة التحكم...',
          icon: 'CheckCircle',
          iconColor: 'var(--color-success)',
        });
        setTimeout(() => navigate('/photographer-dashboard'), 1500);
      };

      if (data?.redirectUrl) {
        const url = new URL(data.redirectUrl);
        const tokenHash = url.searchParams.get('token');
        if (!tokenHash) throw new Error('فشل الحصول على رمز الدخول');
        const { data: authData, error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'email',
        });
        if (verifyError) throw new Error(verifyError.message);
        await finalizeRegistration(authData?.user?.id);
        return;
      }
      if (data?.success && data?.userId) {
        await finalizeRegistration(data.userId);
        return;
      }
      throw new Error('فشل التحقق');
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'كود التحقق غير صحيح');
    } finally {
      setLoading(false);
      setProcessingMessage('');
    }
  }, [otpCode, normalizedPhone, msegatId, formData, navigate]);

  const handleOtpComplete = useCallback(
    (code) => {
      if (loading || uploadingDocument || uploadingCertificates || verifyInFlightRef.current) return;
      verifyInFlightRef.current = true;
      verifyAndRegister(code).finally(() => {
        verifyInFlightRef.current = false;
      });
    },
    [loading, uploadingDocument, uploadingCertificates, verifyAndRegister]
  );

  const resendOTP = () => {
    if (countdown > 0) return;
    setOtpCode('');
    sendOTP();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 py-8 px-4" dir="rtl">
      <div className="max-w-3xl mx-auto">
        <div className="mb-4">
          <BackButton to="/" label="الرئيسية" />
        </div>
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-3 shadow-lg border-2 border-primary/20">
            <AppIcon name="Camera" size={32} className="text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">انضم كمصور محترف</h1>
          <p className="text-slate-600">ابدأ في استقبال طلبات الحجز من العملاء</p>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 transition-all ${
                    currentStep >= step.id
                      ? 'bg-primary text-white shadow-lg'
                      : 'bg-slate-200 text-slate-500'
                  }`}>
                    <AppIcon name={step.icon} size={18} />
                  </div>
                  <span className={`text-xs font-medium ${currentStep >= step.id ? 'text-slate-900' : 'text-slate-500'}`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded-full ${
                    currentStep > step.id ? 'bg-primary' : 'bg-slate-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2">
                <AppIcon name="AlertCircle" size={18} className="text-red-600" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <AppIcon name="User" size={22} className="text-primary" />
                البيانات الأساسية
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">الاسم الاول والاخير  *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="محمد أحمد"
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-primary focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">البريد الإلكتروني *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="example@email.com"
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-primary focus:outline-none text-left"
                    dir="ltr"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">رقم الجوال *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="05xxxxxxxx"
                    className={`w-full px-4 py-3 rounded-xl border-2 text-left focus:outline-none ${
                      ((formData.phone || '').replace(/\\D/g, '').length === 10 || !formData.phone)
                        ? 'border-slate-200 focus:border-primary'
                        : 'border-red-500 focus:border-red-500'
                    }`}
                    dir="ltr"
                  />
                </div>

                {/* نوع الحساب: فرد أو استوديو — اسم الاستوديو يظهر فقط عند اختيار "استوديو" */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">نوع الحساب</label>
                  <div className="flex gap-3 flex-wrap">
                    <label className="flex-1 min-w-[140px] cursor-pointer">
                      <input
                        type="radio"
                        name="entityType"
                        value="individual"
                        checked={formData.entityType === 'individual'}
                        onChange={(e) => {
                          handleChange(e);
                          if (e.target.checked) setFormData((prev) => ({ ...prev, businessName: '' }));
                        }}
                        className="sr-only peer"
                      />
                      <span className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-slate-200 transition-all peer-checked:border-primary peer-checked:bg-primary/5 peer-checked:text-primary font-medium">
                        <AppIcon name="User" size={20} />
                        فرد
                      </span>
                    </label>
                    {/* COMING_SOON_STUDIO: لتفعيل استوديو لاحقاً، استبدل هذا الـ div بالـ label الأصلي */}
                    <div className="flex-1 min-w-[140px] cursor-not-allowed opacity-60 relative">
                      <span className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-slate-200 font-medium text-slate-400">
                        <AppIcon name="Building2" size={20} />
                        استوديو
                      </span>
                      <span className="absolute -top-2 -left-2 text-[10px] font-bold bg-amber-400 text-white px-1.5 py-0.5 rounded-full">
                        قريباً
                      </span>
                    </div>
                  </div>
                </div>

                {formData.entityType === 'entity' && (
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">اسم الاستوديو *</label>
                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      placeholder="استوديو التصوير"
                      required
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-primary focus:outline-none"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Work Info */}
          {currentStep === 2 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <AppIcon name="Briefcase" size={22} className="text-primary" />
                معلومات العمل
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">المدينة *</label>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-primary focus:outline-none"
                  >
                    <option value="">اختر المدينة</option>
                    {cities.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
              </div>

            </div>
          )}

          {/* Step 3: Specializations */}
          {currentStep === 3 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <AppIcon name="Camera" size={22} className="text-primary" />
                التخصصات والمعدات
              </h2>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-3">التخصصات *</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {specializations.map((spec) => (
                    <button
                      key={spec.value}
                      type="button"
                      onClick={() => handleMultiSelect(spec.value)}
                      className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                        formData.specializations.includes(spec.value)
                          ? 'bg-primary text-white border-blue-500'
                          : 'bg-white border-slate-200 hover:border-blue-300'
                      }`}
                    >
                      {spec.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Shooting Methods - Required */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-3">
                  وسائل التصوير <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-slate-500 mb-3">
                  اختر وسيلة أو أكثر من وسائل التصوير التي تقدمها (يمكن اختيار أكثر من واحدة)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {shootingMethodOptions.map((method) => {
                    const isSelected = formData.shootingMethods.includes(method.value);
                    return (
                      <div key={method.value} className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setFormData((prev) => {
                                const { [method.value]: _, ...rest } = prev.shootingMethodModels || {};
                                return {
                                  ...prev,
                                  shootingMethods: prev.shootingMethods.filter((v) => v !== method.value),
                                  shootingMethodModels: rest,
                                };
                              });
                            } else {
                              setFormData((prev) => ({
                                ...prev,
                                shootingMethods: [...prev.shootingMethods, method.value],
                              }));
                            }
                          }}
                          className={`p-4 rounded-xl border-2 text-right transition-all hover:border-blue-500 hover:bg-blue-50 ${
                            isSelected ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-slate-200 bg-white'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 border border-blue-100">
                                  <AppIcon name={method.icon} size={20} className="text-primary" />
                                </div>
                                <div className="text-base font-semibold text-slate-900">{method.label}</div>
                              </div>
                            </div>
                            {isSelected && <AppIcon name="CheckCircle2" size={20} className="text-green-500 flex-shrink-0" />}
                          </div>
                        </button>

                        {/* حقل نوع الجهاز — إلزامي عند التحديد */}
                        {isSelected && (
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-semibold text-slate-600">
                              النوع <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.shootingMethodModels?.[method.value] || ''}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  shootingMethodModels: {
                                    ...prev.shootingMethodModels,
                                    [method.value]: e.target.value,
                                  },
                                }))
                              }
                              placeholder={method.placeholder}
                              className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 text-right placeholder:text-slate-400 ${
                                !(formData.shootingMethodModels?.[method.value] || '').trim()
                                  ? 'border-red-300'
                                  : 'border-blue-200'
                              }`}
                            />
                            {!(formData.shootingMethodModels?.[method.value] || '').trim() && (
                              <p className="text-xs text-red-500">هذا الحقل إلزامي</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {formData.shootingMethods.length === 0 && (
                  <p className="text-xs text-red-500 mt-2">يجب اختيار وسيلة تصوير واحدة على الأقل</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">
                  هل تقدم تعديلاً احترافياً لعملائك؟
                </label>
                <p className="text-xs text-slate-500 mb-3">اختيار التعديل الاحترافي يُضاف تلقائياً كرسوم إضافية على سعر جلستك — يمكنك تغيير هذا لاحقاً</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {equipmentOptions.map((equip) => {
                    const isSelected = formData.equipmentAvailable[0] === equip.value;
                    const isPro = equip.value === 'professional-editing';

                    const secondaryLine = isPro
                      ? 'تنقية كاملة، معالجة خلفية، وتفاصيل دقيقة باستخدام برامج متخصصة'
                      : 'تصحيح الإضاءة والألوان والوضوح — سريع ومناسب لمعظم الجلسات';
                    const toolsLine = isPro
                      ? 'Lr · Ps · Pr · DaVinci وما شابهها'
                      : 'Lr · Snapseed · CapCut وما شابهها';

                    return (
                      <button
                        key={equip.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, equipmentAvailable: [equip.value] }))}
                        className={`p-3 rounded-xl border-2 text-right transition-all ${
                          isSelected
                            ? 'border-amber-500 bg-amber-50'
                            : 'bg-white border-slate-200 hover:border-amber-300'
                        }`}
                      >
                        <div className="font-semibold text-sm mb-0.5 text-slate-800">
                          {isPro ? 'نعم، أقدم تعديل احترافي' : 'لا، اقدم التعديل الأساسي فقط'}
                        </div>
                        <div className="text-xs text-slate-500">
                          {secondaryLine}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
                          {toolsLine}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {formData.equipmentAvailable.length === 0 && (
                  <p className="text-xs text-amber-600 mt-2">⚠️ يجب تحديد مستوى التعديل للمتابعة</p>
                )}
              </div>

              {/* SAFE MODE: Certificates feature - optional photography certificates */}
              <div className="border-t border-slate-200 pt-5 mt-5">
                <h3 className="text-sm font-bold text-slate-700 mb-1">شهادات التصوير (اختياري)</h3>
                <p className="text-xs text-slate-500 mb-3">يمكنك رفع شهاداتك لزيادة الثقة لدى العملاء</p>
                {certificateFiles.length > 0 ? (
                  <div className="space-y-2 mb-3">
                    {certificateFiles.map((file, index) => (
                      <div key={index} className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <AppIcon name="FileText" size={18} className="text-slate-500 flex-shrink-0" />
                          <span className="text-sm text-slate-700 truncate">{file.name}</span>
                          <span className="text-xs text-slate-400 flex-shrink-0">{(file.size / 1024).toFixed(1)} KB</span>
                        </div>
                        <button type="button" onClick={() => removeCertificateFile(index)} className="p-2 text-red-500 hover:bg-red-100 rounded-lg flex-shrink-0">
                          <AppIcon name="Trash2" size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div
                  onClick={() => certificateInputRef.current?.click()}
                  className="border-2 border-dashed rounded-xl p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 border-slate-300"
                >
                  <AppIcon name="Upload" size={24} className="text-slate-400 mx-auto mb-1" />
                  <p className="text-sm text-slate-600">رفع شهادات (PDF، JPG، PNG — حد أقصى 5MB لكل ملف)</p>
                </div>
                <input
                  type="file"
                  ref={certificateInputRef}
                  onChange={handleCertificateSelect}
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  multiple
                  className="hidden"
                />
              </div>

              {/* Document Upload (Required) */}
              <div className="border-t border-slate-200 pt-5 mt-5">
                <h3 className="text-sm font-bold text-slate-700 mb-1 flex items-center gap-2">
                  <AppIcon name="FileCheck" size={18} className="text-green-600" />
                  توثيق الهوية <span className="text-red-500">*</span>
                </h3>
                <p className="text-xs text-slate-500 mb-3">
                  المستندات المقبولة: بطاقة الهوية الوطنية أو جواز السفر أو السجل التجاري (إذا كنت مؤسسة أو شركة)
                </p>

                {identityDocument ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      {documentPreview ? (
                        <img src={documentPreview} alt="Preview" className="w-16 h-16 rounded-lg object-cover" />
                      ) : (
                        <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center">
                          <AppIcon name="FileText" size={24} className="text-red-500" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-700">{identityDocument.name}</p>
                        <p className="text-xs text-slate-500">{(identityDocument.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveDocument}
                        className="p-2 text-red-500 hover:bg-red-100 rounded-lg"
                      >
                        <AppIcon name="Trash2" size={18} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => documentInputRef.current?.click()}
                    className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 border-slate-300"
                  >
                    <AppIcon name="Upload" size={32} className="text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">ارفع مستند الهوية (إجباري)</p>
                    <p className="text-xs text-slate-400">هوية وطنية / جواز سفر / سجل تجاري - JPEG, PNG, PDF (حتى 10MB)</p>
                  </div>
                )}
                <input
                  type="file"
                  ref={documentInputRef}
                  onChange={handleDocumentSelect}
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="hidden"
                />
              </div>

              {/* Terms */}
              <div className="pt-4">
                <div
                  className={`rounded-xl border-2 transition-all duration-200 ${
                    formData.agreeToTerms
                      ? 'border-primary/40 bg-primary/5'
                      : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <label className="flex items-start gap-3 p-4 cursor-pointer select-none">
                    <div className="relative mt-0.5 flex-shrink-0">
                      <input
                        type="checkbox"
                        id="agreeToTerms"
                        name="agreeToTerms"
                        className="sr-only"
                        checked={formData.agreeToTerms}
                        onChange={handleChange}
                      />
                      <div
                        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                          formData.agreeToTerms ? 'bg-primary border-primary' : 'bg-white border-slate-300'
                        }`}
                      >
                        {formData.agreeToTerms && (
                          <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="text-sm text-slate-700 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                      أقر بأنني قرأت وأوافق على{' '}
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openTermsModal(); }}
                        className="text-primary font-semibold underline underline-offset-2 hover:text-primary/80 transition-colors"
                      >
                        الشروط والأحكام
                      </button>
                      {' '}و{' '}
                      <Link to="/privacy" target="_blank" className="text-primary font-semibold underline underline-offset-2 hover:text-primary/80 transition-colors">
                        سياسة الخصوصية
                      </Link>
                      {' '}الخاصة بمنصة مشهد
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Verification */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-primary/20">
                  <AppIcon name="ShieldCheck" size={40} className="text-primary" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">تأكيد الحساب</h2>
                <p className="text-slate-500 mt-2">
                  {!otpSent 
                    ? `سيتم إرسال كود التحقق إلى: ${normalizedPhone || formData.phone}`
                    : 'أدخل كود التحقق المُرسل إلى جوالك'
                  }
                </p>
              </div>

              {!otpSent ? (
                <button
                  onClick={sendOTP}
                  disabled={loading || !normalizedPhone}
                  className="w-full py-4 bg-primary text-primary-foreground font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <AppIcon name="Loader" size={20} className="animate-spin" />
                  ) : (
                    <>
                      <AppIcon name="Send" size={20} />
                      إرسال كود التحقق
                    </>
                  )}
                </button>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-4 text-center">
                      أدخل كود التحقق المكون من 4 أرقام
                    </label>
                    <OtpInput
                      value={otpCode}
                      onChange={setOtpCode}
                      onComplete={handleOtpComplete}
                      disabled={loading || uploadingDocument || uploadingCertificates}
                      autoFocus={otpSent}
                    />
                  </div>

                  <button
                    onClick={verifyAndRegister}
                    disabled={loading || uploadingDocument || uploadingCertificates || otpCode.length !== 4}
                    className="w-full py-4 bg-primary text-primary-foreground font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading || uploadingDocument || uploadingCertificates ? (
                      <>
                        <AppIcon name="Loader" size={20} className="animate-spin" />
                        {uploadingDocument ? 'جاري رفع المستند...' : uploadingCertificates ? 'جاري رفع الشهادات...' : 'جاري التسجيل...'}
                      </>
                    ) : (
                      <>
                        <AppIcon name="Check" size={20} />
                        إكمال التسجيل
                      </>
                    )}
                  </button>
                  {processingMessage && (
                    <p className="mt-3 text-xs text-slate-500 text-center">
                      {processingMessage}
                    </p>
                  )}

                  <div className="text-center">
                    {countdown > 0 ? (
                      <p className="text-slate-500">
                        إعادة الإرسال بعد <span className="font-bold text-primary">{countdown}</span> ثانية
                      </p>
                    ) : (
                      <button onClick={resendOTP} className="text-primary font-bold hover:underline">
                        إعادة إرسال الكود
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between gap-4 pt-6 mt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="px-6 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-medium hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2"
            >
              <AppIcon name="ChevronRight" size={18} />
              السابق
            </button>

            {currentStep < 4 && (
              <button
                type="button"
                onClick={handleNext}
                className="px-6 py-3 rounded-xl bg-primary text-white font-medium hover:shadow-lg flex items-center gap-2"
              >
                التالي
                <AppIcon name="ChevronLeft" size={18} />
              </button>
            )}
          </div>

          {/* Login Link */}
          <div className="mt-6 text-center pt-4 border-t border-slate-200">
            <p className="text-slate-600">
              لديك حساب؟{' '}
              <Link to="/photographer-login" className="text-primary font-bold hover:underline">
                تسجيل الدخول
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toastNotification && (
        <Toast
          notification={toastNotification}
          onClose={() => setToastNotification(null)}
          duration={5000}
        />
      )}

      {/* ── Modal الشروط والأحكام ─────────────────────────────────────────── */}
      {showTermsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          dir="rtl"
          style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
        >
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowTermsModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <AppIcon name="FileText" size={18} className="text-primary" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">الشروط والأحكام</h2>
              </div>
              <button
                onClick={() => setShowTermsModal(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <AppIcon name="X" size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {termsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
                    <p className="text-sm text-slate-500">جاري التحميل...</p>
                  </div>
                </div>
              ) : termsContent ? (
                <div className="terms-reg-content text-right" dangerouslySetInnerHTML={{ __html: termsContent }} />
              ) : (
                <div className="text-center py-12">
                  <AppIcon name="FileText" size={36} className="text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">لم يتم إضافة محتوى الشروط والأحكام بعد</p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
              <button
                onClick={() => {
                  setFormData(prev => ({ ...prev, agreeToTerms: true }));
                  setShowTermsModal(false);
                }}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-xl font-bold text-sm hover:bg-primary/90 transition-colors"
              >
                أوافق على الشروط والأحكام
              </button>
              <button
                onClick={() => setShowTermsModal(false)}
                className="px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl font-semibold text-sm hover:bg-slate-100 transition-colors"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .terms-reg-content h2 {
          font-size: 1.15rem; font-weight: 700; color: #0f172a;
          margin-top: 1.5rem; margin-bottom: 0.5rem;
          padding-bottom: 0.35rem; border-bottom: 2px solid #e2e8f0;
          font-family: 'IBM Plex Sans Arabic', sans-serif;
        }
        .terms-reg-content h2:first-child { margin-top: 0; }
        .terms-reg-content h3 {
          font-size: 0.95rem; font-weight: 700; color: #1e293b;
          margin-top: 1.1rem; margin-bottom: 0.35rem;
          font-family: 'IBM Plex Sans Arabic', sans-serif;
        }
        .terms-reg-content p {
          color: #475569; line-height: 1.85; margin-bottom: 0.8rem;
          font-size: 0.88rem; font-family: 'IBM Plex Sans Arabic', sans-serif;
        }
        .terms-reg-content ul, .terms-reg-content ol { padding-right: 1.2rem; margin-bottom: 0.8rem; }
        .terms-reg-content li {
          color: #475569; line-height: 1.85; margin-bottom: 0.25rem;
          font-size: 0.88rem; font-family: 'IBM Plex Sans Arabic', sans-serif;
        }
        .terms-reg-content strong { color: #1e293b; font-weight: 700; }
      `}</style>
    </div>
  );
};

export default PhotographerRegistration;
